import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { goodsSales, type GoodsSaleItem } from '@/api/goodsSales';
import DocumentToolbar from '@/shared/components/DocumentToolbar';
import { customerOrders } from '@/api/orders';
import { orgs, contractors as contractorsApi, nomenclature as nomApi } from '@/api/catalogs';
import { refs } from '@/api/refs';
import { useConfirm } from '@/components/ConfirmProvider';
import ProjectSelect from '@/components/ProjectSelect';
import { useNumberPreview } from '@/hooks/useNumberPreview';
import { usePermissionsByOrg } from '@/hooks/usePermissionsByOrg';
import { usePermissions } from '@/auth/usePermissions';
import CommissionerWarningModal, {
  type CommissionerWarningChoice,
} from '@/components/CommissionerWarningModal';
import NomenclaturePickerModal from '@/components/NomenclaturePickerModal';
import DatePopover from '@/components/DatePopover';
import type { GoodsBalanceRow } from '@/api/documents';
import {
  commissionerWarning,
  type CommissionerWarningCheck,
} from '@/api/commissionerWarning';

interface FormState {
  organization_id: string;
  document_number: string;
  document_date: string;
  contractor_id: string;
  contract_id: string;
  warehouse_id: string;
  customer_order_id: string;
  allow_overshipment: boolean;
  prices_include_vat: boolean;
  delivery_address: string;
  comment: string;
  project_id: string | null;
  items: GoodsSaleItem[];
}

const EMPTY_FORM: FormState = {
  organization_id: '',
  document_number: '',
  document_date: new Date().toISOString().slice(0, 16),
  contractor_id: '',
  contract_id: '',
  warehouse_id: '',
  customer_order_id: '',
  allow_overshipment: false,
  prices_include_vat: false,
  delivery_address: '',
  comment: '',
  project_id: null,
  items: [],
};

const newRow = (
  n: number,
  vatId: string,
  defaultTaxPurposeId: string | null = null,
): GoodsSaleItem => ({
  line_number: n, nomenclature_id: '', quantity: '0', price: '0',
  amount_without_vat: '0', vat_rate_id: vatId, vat_amount: '0', total_amount: '0',
  // 2026-06-02: default TAXABLE (за рішенням власника).
  tax_purpose_id: defaultTaxPurposeId,
});

const fmt = (n: number) => n.toLocaleString('uk-UA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function GoodsSaleEditor() {
  const confirm = useConfirm();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { id } = useParams();
  const isNew = !id || id === 'new';
  const { has } = usePermissions();

  const orgList = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  // UX α: фільтр org dropdown по sales.realization.create permission
  const {
    availableOrgs,
    hasPermissionInAnyOrg,
    totalAccessibleOrgs,
    loading: permsLoading,
  } = usePermissionsByOrg('sales.realization.create');
  const buyerList = useQuery({
    queryKey: ['buyers'],
    queryFn: () => contractorsApi.list({ limit: 500 }),
  });
  const nomList = useQuery({
    queryKey: ['nomenclature-full'],
    queryFn: () => nomApi.list({ limit: 500 }),
  });
  const vatList = useQuery({ queryKey: ['vat-rates'], queryFn: refs.vatRates });
  // TD-B-P5.5.3 (2026-06-01): tax_purposes для UI dropdown.
  const taxPurposesList = useQuery({ queryKey: ['tax-purposes'], queryFn: refs.taxPurposes });
  const docQuery = useQuery({
    queryKey: ['goods-sale', id],
    queryFn: () => goodsSales.get(id!),
    enabled: !isNew,
  });

  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const numberPreview = useNumberPreview('doc.goods_sales', form.organization_id, isNew);
  const [error, setError] = useState<string | null>(null);
  // Block 7.6: warning modal state.
  const [commWarning, setCommWarning] = useState<CommissionerWarningCheck | null>(null);
  // 2026-06-02: Picker modal для додавання товару з залишків.
  const [pickerOpen, setPickerOpen] = useState(false);

  const warehousesQ = useQuery({
    queryKey: ['warehouses', form.organization_id],
    queryFn: () => refs.warehouses(form.organization_id),
    enabled: !!form.organization_id,
  });
  const contractsQ = useQuery({
    queryKey: ['contracts', form.organization_id, form.contractor_id],
    queryFn: () => refs.contracts({
      organization_id: form.organization_id,
      contractor_id: form.contractor_id,
    }),
    enabled: !!form.organization_id && !!form.contractor_id,
  });
  // Активные заказы покупателя (для селекта «На основании заказа»)
  const customerOrdersQ = useQuery({
    queryKey: ['customer-orders-active', form.organization_id, form.contractor_id],
    queryFn: () => customerOrders.list({
      organization_id: form.organization_id,
      contractor_id: form.contractor_id,
      posted: true,
      limit: 100,
    }),
    enabled: !!form.organization_id && !!form.contractor_id,
  });

  const defaultVatId = useMemo(() => {
    const v = vatList.data?.find((x) => x.rate_percent === 20);
    return v?.id ?? '';
  }, [vatList.data]);
  // 2026-06-02: default TAXABLE для tax_purpose у новий рядок.
  const defaultTaxPurposeId = useMemo(
    () => taxPurposesList.data?.find((tp) => tp.code === 'TAXABLE')?.id ?? null,
    [taxPurposesList.data],
  );
  // 2026-06-02: ховаємо колонки ПДВ якщо org НЕ платник.
  const currentOrg = useMemo(
    () => orgList.data?.items.find((o) => o.id === form.organization_id),
    [orgList.data, form.organization_id],
  );
  const showVatColumns = currentOrg?.is_vat_payer ?? true;
  const exemptVatId = useMemo(() => {
    const v = vatList.data?.find((x) => x.code === 'EXEMPT' || x.code === 'VAT_0');
    return v?.id ?? '';
  }, [vatList.data]);
  const effectiveDefaultVatId = showVatColumns ? defaultVatId : exemptVatId;

  // Карта nomenclature_id -> use_series. Для рядків, де use_series=true,
  // показуємо селект серії.
  const useSeriesMap = useMemo(() => {
    const m = new Map<string, boolean>();
    nomList.data?.items.forEach((n) => m.set(n.id, !!n.use_series));
    return m;
  }, [nomList.data]);
  // 2026-06-02: карта nomenclature_id -> base_uom_id для auto-fill.
  const baseUomByNomId = useMemo(() => {
    const m = new Map<string, string>();
    for (const n of nomList.data?.items ?? []) {
      if (n.base_uom_id) m.set(n.id, n.base_uom_id);
    }
    return m;
  }, [nomList.data]);
  // Етап 2.1: карта nomenclature_id -> vat_rate_id (ставка ПДВ з картки).
  // При виборі номенклатури підставляємо ставку за замовчуванням у рядок.
  const vatRateByNomId = useMemo(() => {
    const m = new Map<string, string>();
    for (const n of nomList.data?.items ?? []) {
      if (n.vat_rate_id) m.set(n.id, n.vat_rate_id);
    }
    return m;
  }, [nomList.data]);
  // 2026-06-02: soft-warning — балансі на дату документа для перевірки
  // що quantity не перевищує доступну на складі.
  const balanceForWarningQ = useQuery({
    queryKey: ['goods-balance-warning', form.organization_id, form.document_date, form.warehouse_id],
    queryFn: () => import('@/api/documents').then((m) =>
      m.reports.goodsBalance({
        organization_id: form.organization_id,
        on_date: form.document_date,
        warehouse_id: form.warehouse_id || undefined,
      })
    ),
    enabled: !!form.organization_id && !!form.document_date && !!form.warehouse_id,
  });
  const availableByNomId = useMemo(() => {
    const m = new Map<string, number>();
    for (const r of balanceForWarningQ.data?.rows ?? []) {
      // Якщо одна номенклатура є у кількох складах і warehouse не обрано —
      // підсумовуємо. У більшості випадків warehouse_id обраний.
      const prev = m.get(r.nomenclature_id) ?? 0;
      m.set(r.nomenclature_id, prev + Number(r.quantity));
    }
    return m;
  }, [balanceForWarningQ.data]);

  // Запити серій по тих номенклатурах, які зустрічаються в рядках
  // і мають use_series=true.
  const seriesNomIds = useMemo(() => {
    const s = new Set<string>();
    for (const it of form.items) {
      if (it.nomenclature_id && useSeriesMap.get(it.nomenclature_id)) {
        s.add(it.nomenclature_id);
      }
    }
    return Array.from(s);
  }, [form.items, useSeriesMap]);

  const seriesQueries = useQueries({
    queries: seriesNomIds.map((nid) => ({
      queryKey: ['nomenclature-series', nid],
      queryFn: () => refs.nomenclatureSeries(nid),
      enabled: !!nid,
      staleTime: 60 * 1000,
    })),
  });
  const seriesByNom = useMemo(() => {
    const m: Record<string, { id: string; name: string; code: string | null }[]> = {};
    seriesNomIds.forEach((nid, idx) => {
      const q = seriesQueries[idx];
      if (q?.data) m[nid] = q.data;
    });
    return m;
  }, [seriesNomIds, seriesQueries]);

  useEffect(() => {
    if (isNew) {
      const firstOrg = availableOrgs[0]?.id ?? '';
      setForm((f) => f.organization_id ? f : {
        ...f,
        organization_id: firstOrg,
        items: f.items.length ? f.items : [newRow(1, effectiveDefaultVatId, defaultTaxPurposeId)],
      });
    } else if (docQuery.data) {
      const d = docQuery.data;
      setForm({
        organization_id: d.organization_id,
        document_number: d.document_number,
        document_date: d.document_date.slice(0, 16),
        contractor_id: d.contractor_id,
        contract_id: d.contract_id,
        warehouse_id: d.warehouse_id,
        customer_order_id: d.customer_order_id ?? '',
        allow_overshipment: d.allow_overshipment,
        prices_include_vat: d.prices_include_vat,
        delivery_address: d.delivery_address ?? '',
        comment: d.comment ?? '',
        project_id: d.project_id ?? null,
        items: d.items.map((it) => ({
          line_number: it.line_number,
          nomenclature_id: it.nomenclature_id,
          series_id: it.series_id ?? null,
          quantity: String(it.quantity),
          price: String(it.price),
          amount_without_vat: String(it.amount_without_vat),
          vat_rate_id: it.vat_rate_id,
          vat_amount: String(it.vat_amount),
          total_amount: String(it.total_amount),
          cost_amount: it.cost_amount ? String(it.cost_amount) : undefined,
          // TD-B-P5.5.3 (2026-06-01).
          tax_purpose_id: it.tax_purpose_id ?? null,
          // 2026-06-02: auto-fill uom_id з base_uom_id.
          uom_id: it.uom_id ?? null,
        })),
      });
    }
  }, [isNew, docQuery.data, availableOrgs, defaultVatId]);

  const isPosted = !isNew && (docQuery.data?.posted ?? false);

  const totalWithoutVat = form.items.reduce((s, e) => s + Number(e.amount_without_vat || 0), 0);
  const totalVat = form.items.reduce((s, e) => s + Number(e.vat_amount || 0), 0);
  const totalAmount = totalWithoutVat + totalVat;
  const totalCost = form.items.reduce((s, e) => s + Number(e.cost_amount || 0), 0);

  const create = useMutation({
    mutationFn: () => goodsSales.create({
      organization_id: form.organization_id,
      document_number: form.document_number,
      document_date: new Date(form.document_date).toISOString(),
      contractor_id: form.contractor_id,
      contract_id: form.contract_id || null,
      warehouse_id: form.warehouse_id,
      customer_order_id: form.customer_order_id || null,
      allow_overshipment: form.allow_overshipment,
      prices_include_vat: form.prices_include_vat,
      delivery_address: form.delivery_address || null,
      comment: form.comment || null,
      project_id: form.project_id,
      items: form.items,
    }),
    onSuccess: (doc) => {
      qc.invalidateQueries({ queryKey: ['goods-sales'] });
      navigate(`/goods-sales/${doc.id}`);
    },
    onError: (e) => setError(extractError(e)),
  });
  const update = useMutation({
    mutationFn: () => goodsSales.update(id!, {
      document_number: form.document_number,
      document_date: new Date(form.document_date).toISOString(),
      contractor_id: form.contractor_id,
      contract_id: form.contract_id || null,
      warehouse_id: form.warehouse_id,
      customer_order_id: form.customer_order_id || null,
      allow_overshipment: form.allow_overshipment,
      prices_include_vat: form.prices_include_vat,
      delivery_address: form.delivery_address || null,
      comment: form.comment || null,
      project_id: form.project_id,
      items: form.items,
    }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['goods-sale', id] }),
    onError: (e) => setError(extractError(e)),
  });
  const post = useMutation({
    mutationFn: () => goodsSales.post(id!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['goods-sale', id] });
      qc.invalidateQueries({ queryKey: ['goods-sales'] });
    },
    onError: (e) => setError(extractError(e)),
  });
  const unpost = useMutation({
    mutationFn: () => goodsSales.unpost(id!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['goods-sale', id] });
      qc.invalidateQueries({ queryKey: ['goods-sales'] });
    },
    onError: (e) => setError(extractError(e)),
  });
  const remove = useMutation({
    mutationFn: () => goodsSales.remove(id!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['goods-sales'] });
      navigate('/goods-sales');
    },
  });

  // Block 7.6: pre-POST commissioner detection. Якщо є активний RECEIVED
  // commission_contract з contractor → показуємо modal; інакше POST одразу.
  async function onPostClick() {
    if (!form.organization_id || !form.contractor_id) {
      post.mutate();
      return;
    }
    try {
      const check = await commissionerWarning.check(
        form.organization_id, form.contractor_id,
      );
      if (check.is_commissioner_relationship) {
        setCommWarning(check);
        return; // wait for user choice via modal
      }
    } catch {
      // detection-fail — не блокуємо POST, fallback на стандартний flow.
    }
    post.mutate();
  }

  function onCommissionerChoice(choice: CommissionerWarningChoice) {
    setCommWarning(null);
    if (choice === 'cancel') return;
    if (choice === 'create_commission') {
      navigate('/documents/commission-sales/new', {
        state: {
          counterparty_id: form.contractor_id,
          organization_id: form.organization_id,
          items: form.items,
        },
      });
      return;
    }
    // 'continue' → proceed with goods_sale POST (audit note додається у backend)
    post.mutate();
  }

  function setItem(i: number, patch: Partial<GoodsSaleItem>) {
    setForm((f) => ({
      ...f,
      items: f.items.map((e, idx) => {
        if (idx !== i) return e;
        const next = { ...e, ...patch };
        const qty = Number(next.quantity) || 0;
        const price = Number(next.price) || 0;
        const vat = vatList.data?.find((v) => v.id === next.vat_rate_id);
        const rate = vat?.rate_percent ?? 0;
        if ('quantity' in patch || 'price' in patch || 'vat_rate_id' in patch) {
          const awv = qty * price;
          const vatAmt = (awv * rate) / 100;
          next.amount_without_vat = awv.toFixed(2);
          next.vat_amount = vatAmt.toFixed(2);
          next.total_amount = (awv + vatAmt).toFixed(2);
        }
        return next;
      }),
    }));
  }
  function addRow() {
    setForm((f) => ({ ...f, items: [...f.items, newRow(f.items.length + 1, effectiveDefaultVatId, defaultTaxPurposeId)] }));
  }
  function addRowFromPicker(picked: GoodsBalanceRow) {
    setForm((f) => {
      const nextLine = f.items.length + 1;
      const row: GoodsSaleItem = {
        ...newRow(nextLine, effectiveDefaultVatId, defaultTaxPurposeId),
        nomenclature_id: picked.nomenclature_id,
        // Auto-fill uom_id (2026-06-02).
        uom_id: baseUomByNomId.get(picked.nomenclature_id) ?? null,
        // Етап 2.1: ставка ПДВ з картки (fallback — дефолт документа).
        vat_rate_id: vatRateByNomId.get(picked.nomenclature_id) ?? effectiveDefaultVatId,
      };
      return {
        ...f,
        items: [...f.items, row],
        // Якщо склад документа порожній — беремо склад з picked.
        warehouse_id: f.warehouse_id || picked.warehouse_id,
      };
    });
    setPickerOpen(false);
  }
  function removeRow(i: number) {
    setForm((f) => ({
      ...f,
      items: f.items.filter((_, idx) => idx !== i).map((e, idx) => ({ ...e, line_number: idx + 1 })),
    }));
  }

  // Загружает строки из выбранного заказа покупателя в форму.
  // Берёт остаток (quantity - quantity_shipped) для каждой строки заказа,
  // пропускает уже полностью отгруженные.
  async function fillFromOrder() {
    if (!form.customer_order_id) return;
    try {
      const order = await customerOrders.get(form.customer_order_id);
      const newItems: GoodsSaleItem[] = order.items
        .map((it, idx) => {
          const remaining = Number(it.quantity) - Number(it.quantity_shipped ?? 0);
          if (remaining <= 0) return null;
          const price = Number(it.price);
          const awv = remaining * price;
          const vat = vatList.data?.find((v) => v.id === it.vat_rate_id);
          const rate = vat?.rate_percent ?? 0;
          const vatAmt = (awv * rate) / 100;
          const row: GoodsSaleItem = {
            line_number: idx + 1,
            nomenclature_id: it.nomenclature_id,
            quantity: String(remaining),
            price: String(price),
            amount_without_vat: awv.toFixed(2),
            // Ставка з замовлення; якщо порожня — з картки номенклатури (Етап 2.1).
            vat_rate_id: it.vat_rate_id ?? vatRateByNomId.get(it.nomenclature_id) ?? effectiveDefaultVatId,
            vat_amount: vatAmt.toFixed(2),
            total_amount: (awv + vatAmt).toFixed(2),
            uom_id: baseUomByNomId.get(it.nomenclature_id) ?? null,
            tax_purpose_id: defaultTaxPurposeId,
          };
          return row;
        })
        .filter((x): x is GoodsSaleItem => x !== null);
      if (newItems.length === 0) {
        alert('Замовлення повністю відвантажено — немає що додавати.');
        return;
      }
      setForm((f) => ({ ...f, items: newItems }));
    } catch (e) {
      setError(extractError(e));
    }
  }

  // UX α: warning якщо новий документ а user не має sales.realization.create
  // у жодній org
  if (isNew && !permsLoading && !hasPermissionInAnyOrg) {
    return (
      <div className="card">
        <div style={{ padding: '1.5rem', textAlign: 'center' }}>
          <h2 style={{ marginTop: 0 }}>{t('gs.new')}</h2>
          <div style={{
            padding: '1rem', background: '#fef3c7', borderRadius: 6,
            border: '1px solid #fbbf24', color: '#92400e',
            maxWidth: 600, margin: '1rem auto',
          }}>
            ⚠ У вас немає прав створювати реалізацію у жодній з ваших організацій
            ({totalAccessibleOrgs}). Зверніться до адміністратора tenant'а.
          </div>
          <button onClick={() => navigate('/goods-sales')}>{t('common.cancel')}</button>
        </div>
      </div>
    );
  }

  return (
    <div className="card">
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>
          {isNew ? t('gs.new') : `${t('gs.title')}: ${form.document_number}`}
          {isPosted && <span style={{ marginLeft: '0.75rem', color: '#15803d', fontSize: '1rem' }}>✓ {t('mo.posted_yes')}</span>}
        </h2>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
          <DocumentToolbar
            onBackToList={() => navigate('/goods-sales')}
            onSave={() => isNew ? create.mutate() : update.mutate()}
            saveDisabled={create.isPending || update.isPending || !has('sales.realization.create')}
            savePending={create.isPending || update.isPending}
            isPosted={isPosted}
            isNew={isNew}
            onPost={onPostClick}
            postPending={post.isPending}
            postDisabled={form.items.length === 0 || !has('sales.realization.post')}
            onUnpost={() => unpost.mutate()}
            unpostPending={unpost.isPending}
            onDelete={() => { confirm(t('common.confirm_delete')).then((ok) => { if (ok) remove.mutate(); }); }}
            deletePending={remove.isPending}
            deleteDisabled={!has('sales.realization.delete')}
            onPrint={() => navigate(`/goods-sales/${id}/print`)}
          />
          {/* 2026-06-03: створення ТТН на основі цієї Реалізації. */}
          {!isNew && isPosted && (
            <button
              onClick={() => navigate(`/tnn/new?sale_id=${id}`)}
              title="Створити ТТН на основі цієї Реалізації"
            >
              Створити ТТН
            </button>
          )}
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.75rem' }}>
        <div className="field">
          <label>{t('gs.organization')}</label>
          <select value={form.organization_id}
                  onChange={(e) => setForm({ ...form, organization_id: e.target.value })}
                  disabled={!isNew}>
            <option value="">—</option>
            {(isNew ? availableOrgs : orgList.data?.items ?? []).map((o) => (
              <option key={o.id} value={o.id}>{o.code} — {o.name}</option>
            ))}
          </select>
          {isNew && availableOrgs.length < totalAccessibleOrgs && (
            <small style={{ color: '#64748b', fontSize: '0.75rem' }}>
              Показано {availableOrgs.length} з {totalAccessibleOrgs} ваших організацій
              (інші не дозволяють цю операцію)
            </small>
          )}
        </div>
        <div className="field">
          <label>{t('gs.number')}</label>
          <input value={form.document_number}
                 onChange={(e) => setForm({ ...form, document_number: e.target.value })}
                 placeholder={isNew && numberPreview ? `буде: ${numberPreview}` : ''}
                 disabled={isPosted} />
        </div>
        <div className="field">
          <label>{t('gs.date')}</label>
          <DatePopover withTime value={form.document_date}
                 onChange={(v) => setForm({ ...form, document_date: v })}
                 disabled={isPosted} />
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.75rem' }}>
        <div className="field">
          <label>{t('gs.contractor')}</label>
          <select value={form.contractor_id}
                  onChange={(e) => setForm({ ...form, contractor_id: e.target.value, contract_id: '' })}
                  disabled={isPosted}>
            <option value="">—</option>
            {buyerList.data?.items.map((c) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
        </div>
        <div className="field">
          <label>
            {t('gs.contract')}{' '}
            <small style={{ color: 'var(--muted)' }}>
              (необов'язково — буде створено основний автоматично)
            </small>
          </label>
          <select value={form.contract_id}
                  onChange={(e) => setForm({ ...form, contract_id: e.target.value })}
                  disabled={isPosted || !form.contractor_id}>
            <option value="">без договору</option>
            {contractsQ.data?.map((c) => (
              <option key={c.id} value={c.id}>{c.code} — {c.name}</option>
            ))}
          </select>
        </div>
        <div className="field">
          <label>{t('gs.warehouse')}</label>
          <select value={form.warehouse_id}
                  onChange={(e) => setForm({ ...form, warehouse_id: e.target.value })}
                  disabled={isPosted}>
            <option value="">—</option>
            {warehousesQ.data?.map((w) => (
              <option key={w.id} value={w.id}>{w.code} — {w.name}</option>
            ))}
          </select>
        </div>
      </div>

      {!!form.contractor_id && (() => {
        // ПРОДАЖ-розбивка Етап 1+5 (2026-06-03): розділяємо dropdown
        // по order_kind (рахунки + замовлення окремо), фільтр closing.
        const activeDocs = (customerOrdersQ.data?.items ?? [])
          .filter((o) => (o.closing_status ?? 'OPEN') !== 'CLOSED'
                      && o.state !== 'CANCELLED');
        const invoices = activeDocs.filter((o) => o.order_kind === 'PROFORMA_INVOICE');
        const orders = activeDocs.filter((o) => o.order_kind === 'ORDER');
        return (
          <div style={{ display: 'grid', gridTemplateColumns: '2fr auto', gap: '0.75rem' }}>
            <div className="field">
              <label>На підставі Рахунку або Замовлення (необов'язково)</label>
              <select value={form.customer_order_id}
                      onChange={(e) => setForm({ ...form, customer_order_id: e.target.value })}
                      disabled={isPosted}>
                <option value="">— (без посилання)</option>
                {invoices.length > 0 && (
                  <optgroup label="💰 Рахунки покупцю">
                    {invoices.map((o) => (
                      <option key={o.id} value={o.id}>
                        {o.document_number} від {new Date(o.document_date).toLocaleDateString('uk-UA')}
                        {' · '}{Number(o.total_amount).toLocaleString('uk-UA', { minimumFractionDigits: 2 })} грн
                      </option>
                    ))}
                  </optgroup>
                )}
                {orders.length > 0 && (
                  <optgroup label="📋 Замовлення покупця">
                    {orders.map((o) => (
                      <option key={o.id} value={o.id}>
                        {o.document_number} від {new Date(o.document_date).toLocaleDateString('uk-UA')}
                        {' · '}{Number(o.total_amount).toLocaleString('uk-UA', { minimumFractionDigits: 2 })} грн
                      </option>
                    ))}
                  </optgroup>
                )}
                {activeDocs.length === 0 && (
                  <option disabled>(немає відкритих документів для цього контрагента)</option>
                )}
              </select>
              <small style={{ color: 'var(--muted)', fontSize: '0.75rem' }}>
                Рекомендується вибрати <strong>Рахунок</strong>: при проведенні
                Реалізації резерв автоматично знімається.
              </small>
            </div>
            {!isPosted && form.customer_order_id && (
              <div className="field" style={{ display: 'flex', alignItems: 'flex-end' }}>
                <button onClick={() => void fillFromOrder()}>
                  ⟳ Заповнити з підстави
                </button>
              </div>
            )}
          </div>
        );
      })()}

      {form.customer_order_id && (
        <div className="field" style={{ marginTop: '-0.5rem' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: isPosted ? 'default' : 'pointer' }}>
            <input
              type="checkbox"
              checked={form.allow_overshipment}
              disabled={isPosted}
              onChange={(e) => setForm({ ...form, allow_overshipment: e.target.checked })}
            />
            <span>
              Довідвантаження за згодою керівника
              <span style={{ fontSize: '0.8rem', color: '#64748b', marginLeft: '0.4rem' }}>
                (дозволяє відвантажити більше замовленого або позицію, якої немає в замовленні)
              </span>
            </span>
          </label>
        </div>
      )}
      <div className="field">
        <label>Проект / напрям</label>
        <ProjectSelect
          value={form.project_id}
          organizationId={form.organization_id}
          onChange={(v) => setForm({ ...form, project_id: v })}
          disabled={isPosted} />
      </div>


      <h3 style={{ marginTop: '1.5rem' }}>{t('gs.items')}</h3>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
        <thead>
          <tr style={{ background: '#f8fafc', textAlign: 'left' }}>
            <th style={{ padding: '0.5rem', width: 40 }}>#</th>
            <th style={{ padding: '0.5rem' }}>{t('gs.nomenclature')}</th>
            <th style={{ padding: '0.5rem', width: 90 }}>{t('gs.qty')}</th>
            <th style={{ padding: '0.5rem', width: 110 }}>{t('gs.price')}</th>
            <th style={{ padding: '0.5rem', width: 130 }}>
              {showVatColumns ? t('gs.amount_without_vat') : 'Сума'}
            </th>
            {showVatColumns && (
              <>
                <th style={{ padding: '0.5rem', width: 100 }}>{t('gs.vat')}</th>
                <th style={{ padding: '0.5rem', width: 130 }}
                    title="Призначення ПДВ для пропорційного розподілу (ПКУ ст.199)">
                  Призн. ПДВ
                </th>
                <th style={{ padding: '0.5rem', width: 110 }}>{t('gs.vat_amount')}</th>
                <th style={{ padding: '0.5rem', width: 120 }}>{t('gs.total')}</th>
              </>
            )}
            {isPosted && <th style={{ padding: '0.5rem', width: 120 }}>{t('gs.cost_amount')}</th>}
            <th style={{ padding: '0.5rem', width: 40 }}></th>
          </tr>
        </thead>
        <tbody>
          {form.items.map((it, i) => (
            <>
            <tr key={i} style={{ borderTop: '1px solid #e2e8f0' }}>
              <td style={{ padding: '0.5rem' }}>{it.line_number}</td>
              <td style={{ padding: '0.5rem' }}>
                <select value={it.nomenclature_id}
                        onChange={(e) => {
                          const nomId = e.target.value;
                          setItem(i, {
                            nomenclature_id: nomId,
                            series_id: null,
                            // Auto-fill uom_id з nomenclature.base_uom_id (2026-06-02).
                            uom_id: baseUomByNomId.get(nomId) ?? null,
                            // Етап 2.1: ставка ПДВ з картки (пересчёт vat_amount у setItem).
                            vat_rate_id: vatRateByNomId.get(nomId) ?? it.vat_rate_id,
                          });
                        }}
                        disabled={isPosted}>
                  <option value="">—</option>
                  {nomList.data?.items.map((n) => (
                    <option key={n.id} value={n.id}>{n.code} — {n.name}</option>
                  ))}
                </select>
              </td>
              <td style={{ padding: '0.5rem' }}>
                {(() => {
                  const available = it.nomenclature_id
                    ? availableByNomId.get(it.nomenclature_id)
                    : undefined;
                  const reqQty = Number(it.quantity || 0);
                  const overshoot = available !== undefined && reqQty > available;
                  return (
                    <>
                      <input
                        type="number"
                        step="0.001"
                        value={it.quantity}
                        onChange={(e) => setItem(i, { quantity: e.target.value })}
                        disabled={isPosted}
                        style={overshoot ? {
                          borderColor: '#dc2626',
                          background: '#fef2f2',
                        } : undefined}
                        title={overshoot
                          ? `Доступно: ${available}. POST впаде 422.`
                          : (available !== undefined
                              ? `Доступно: ${available}`
                              : undefined)}
                      />
                      {overshoot && (
                        <div style={{
                          fontSize: '0.7rem',
                          color: '#dc2626',
                          marginTop: 2,
                          whiteSpace: 'nowrap',
                        }}>
                          ⚠ доступно {available}
                        </div>
                      )}
                    </>
                  );
                })()}
              </td>
              <td style={{ padding: '0.5rem' }}>
                <input type="number" step="0.01" value={it.price}
                       onChange={(e) => setItem(i, { price: e.target.value })}
                       disabled={isPosted} />
              </td>
              <td style={{ padding: '0.5rem', textAlign: 'right' }}>{fmt(Number(it.amount_without_vat))}</td>
              {showVatColumns && (
                <>
                  <td style={{ padding: '0.5rem' }}>
                    <select value={it.vat_rate_id}
                            onChange={(e) => setItem(i, { vat_rate_id: e.target.value })}
                            disabled={isPosted}>
                      {vatList.data?.map((v) => (
                        <option key={v.id} value={v.id}>{v.name}</option>
                      ))}
                    </select>
                  </td>
                  {/* TD-B-P5.5.3 (2026-06-01): tax_purpose dropdown */}
                  <td style={{ padding: '0.5rem' }}>
                    <select
                      value={it.tax_purpose_id ?? ''}
                      onChange={(e) => setItem(i, {
                        tax_purpose_id: e.target.value || null,
                      })}
                      disabled={isPosted}
                      style={{ width: '100%' }}
                      title="Залиште порожнім — буде UNCLASSIFIED у пропорційному розподілі"
                    >
                      <option value="">— не вказано —</option>
                      {taxPurposesList.data?.map((tp) => (
                        <option key={tp.id} value={tp.id}>{tp.name_uk}</option>
                      ))}
                    </select>
                  </td>
                  <td style={{ padding: '0.5rem', textAlign: 'right' }}>{fmt(Number(it.vat_amount))}</td>
                  <td style={{ padding: '0.5rem', textAlign: 'right', fontWeight: 600 }}>{fmt(Number(it.total_amount))}</td>
                </>
              )}
              {isPosted && (
                <td style={{ padding: '0.5rem', textAlign: 'right', color: '#64748b' }}>
                  {fmt(Number(it.cost_amount ?? 0))}
                </td>
              )}
              <td style={{ padding: '0.5rem' }}>
                {!isPosted && (
                  <button onClick={() => removeRow(i)} className="danger" style={{ padding: '0.3rem 0.6rem' }}>×</button>
                )}
              </td>
            </tr>
            {it.nomenclature_id && useSeriesMap.get(it.nomenclature_id) && (
              <tr key={`ser-${i}`} style={{ background: '#fefce8', fontSize: '0.8rem' }}>
                <td></td>
                <td colSpan={2} style={{ padding: '0.3rem 0.5rem' }}>
                  <label>
                    <span style={{ color: '#854d0e', marginRight: '0.3rem' }}>Серія:</span>
                    <select
                      value={it.series_id ?? ''}
                      onChange={(e) => setItem(i, { series_id: e.target.value || null })}
                      disabled={isPosted}
                      style={{ minWidth: 220 }}
                    >
                      <option value="">— оберіть серію —</option>
                      {(seriesByNom[it.nomenclature_id] ?? []).map((s) => (
                        <option key={s.id} value={s.id}>
                          {s.code ? `${s.code} — ` : ''}{s.name}
                        </option>
                      ))}
                    </select>
                  </label>
                </td>
                <td colSpan={showVatColumns ? 8 : 4}></td>{/* 8 з ПДВ-колонками, 4 без */}
              </tr>
            )}
            </>
          ))}
        </tbody>
        <tfoot>
          <tr style={{ borderTop: '2px solid #cbd5e1', fontWeight: 600 }}>
            <td colSpan={4} style={{ padding: '0.5rem', textAlign: 'right' }}>{t('gs.total')}:</td>
            <td style={{ padding: '0.5rem', textAlign: 'right' }}>{fmt(totalWithoutVat)}</td>
            {showVatColumns && (
              <>
                <td></td>
                <td></td>{/* tax_purpose column */}
                <td style={{ padding: '0.5rem', textAlign: 'right' }}>{fmt(totalVat)}</td>
                <td style={{ padding: '0.5rem', textAlign: 'right' }}>{fmt(totalAmount)}</td>
              </>
            )}
            {isPosted && (
              <td style={{ padding: '0.5rem', textAlign: 'right', color: '#64748b' }}>{fmt(totalCost)}</td>
            )}
            <td></td>
          </tr>
        </tfoot>
      </table>
      {!isPosted && (
        <div style={{ marginTop: '0.75rem', display: 'flex', gap: '0.5rem' }}>
          <button
            className="primary"
            onClick={() => setPickerOpen(true)}
            disabled={!form.organization_id}
            title={!form.organization_id
              ? 'Спершу оберіть юрособу'
              : 'Підбір товару з залишків з врахуванням складу'}
          >
            + Додати товар з залишків
          </button>
          <button onClick={addRow}>+ Порожній рядок</button>
        </div>
      )}
      {error && <div className="error">{error}</div>}
      {commWarning && (
        <CommissionerWarningModal check={commWarning} onChoose={onCommissionerChoice} />
      )}
      {pickerOpen && form.organization_id && (
        <NomenclaturePickerModal
          organizationId={form.organization_id}
          documentDate={form.document_date}
          defaultWarehouseId={form.warehouse_id || null}
          onPick={addRowFromPicker}
          onClose={() => setPickerOpen(false)}
        />
      )}
    </div>
  );
}

function extractError(e: unknown): string {
  if (typeof e === 'object' && e && 'response' in e) {
    const r = (e as { response?: { data?: { detail?: string } } }).response;
    return r?.data?.detail ?? 'Error';
  }
  return String(e);
}
